/**
 * Page principale des System Alerts - SuperAdmin
 */

import { useState } from 'react';
import { Bell, Activity, AlertTriangle, CheckCircle } from 'lucide-react';
import { useActiveAlerts, useAllAlerts } from '../hooks/useAlerts';
import { AlertsList } from './AlertsList';
import { PerformanceMetricsCard } from './PerformanceMetricsCard';

type Tab = 'dashboard' | 'alerts';

export function SystemAlertsPage() {
  const [tab, setTab] = useState<Tab>('dashboard');

  const { data: activeAlerts } = useActiveAlerts();
  const { data: allAlerts } = useAllAlerts();

  const activeCount = activeAlerts?.length || 0;
  const criticalCount = activeAlerts?.filter((a) => a.severity === 'critical').length || 0;
  const resolvedCount = allAlerts?.filter((a) => a.resolved).length || 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Monitoring & Alertes</h1>
          <p className="text-gray-600 mt-1">
            Surveillez la performance et les alertes systeme
          </p>
        </div>

        {/* Alert Badge */}
        {activeCount > 0 && (
          <div
            className={`px-4 py-2 rounded-xl flex items-center gap-2 ${
              criticalCount > 0
                ? 'bg-red-100 text-red-700 border border-red-200'
                : 'bg-orange-100 text-orange-700 border border-orange-200'
            }`}
          >
            <Bell className="w-5 h-5" />
            <span className="font-semibold">
              {activeCount} alerte{activeCount > 1 ? 's' : ''} active{activeCount > 1 ? 's' : ''}
            </span>
            {criticalCount > 0 && (
              <span className="px-2 py-0.5 bg-red-200 text-red-800 rounded text-xs font-bold">
                {criticalCount} critique{criticalCount > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Alertes Actives</div>
              <div className="text-2xl font-bold text-orange-600">{activeCount}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Bell className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Critiques</div>
              <div className="text-2xl font-bold text-red-600">{criticalCount}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Resolues</div>
              <div className="text-2xl font-bold text-green-600">{resolvedCount}</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-100 rounded-lg">
              <Activity className="w-5 h-5 text-teal-600" />
            </div>
            <div>
              <div className="text-sm text-gray-600">Status</div>
              <div className="text-2xl font-bold text-teal-600">
                {criticalCount > 0 ? 'Degrade' : activeCount > 0 ? 'Attention' : 'OK'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setTab('dashboard')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              tab === 'dashboard'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Activity className="w-4 h-4" />
              Performance
            </div>
          </button>
          <button
            onClick={() => setTab('alerts')}
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors relative ${
              tab === 'alerts'
                ? 'text-teal-600 border-b-2 border-teal-600 bg-teal-50'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <Bell className="w-4 h-4" />
              Alertes
              {activeCount > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-orange-500 text-white rounded-full">
                  {activeCount}
                </span>
              )}
            </div>
          </button>
        </div>

        <div className="p-6">
          {tab === 'dashboard' && <PerformanceMetricsCard />}
          {tab === 'alerts' && <AlertsList />}
        </div>
      </div>
    </div>
  );
}
